import { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axiosInstance from "../../../Config/axiosInstance";
import "./ClientPortfolio.css";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

// Badge vizual pentru statusul clientului.
const StatusPill = ({ value }) => {
  let statusClass = "status-default";
  let statusLabel = value || "";

  if (value === "Active") {
    statusClass = "status-active";
    statusLabel = "Active";
  } else if (value === "Inactive") {
    statusClass = "status-inactive";
    statusLabel = "Inactive";
  } else if (value === "Prospect") {
    statusClass = "status-prospect";
    statusLabel = "Prospect";
  } else if (value === "Blocked") {
    statusClass = "status-blocked";
    statusLabel = "Blocked";
  }

  return <span className={`status-pill ${statusClass}`}>{statusLabel}</span>;
};

function ClientPortfolio() {
  const [rows, setRows] = useState([]);
  const [canView, setCanView] = useState(false);

  const [summary, setSummary] = useState({
    total_clients: 0,
    high_potential_clients: 0,
    with_risk_clients: "-",
    to_be_reactivated_clients: "-",
  });

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  const formatPercentRO = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const n = Number(value);
    if (Number.isNaN(n)) return value;

    return `${new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n)} %`;
  };

  const formatDateRO = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return new Intl.DateTimeFormat("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  useEffect(() => {
    const requiredPath = "/client-portfolio";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const pages = storedPermissions?.pages || [];

    const matchPage = (page) => {
      try {
        return new URL(page.url, window.location.origin).pathname === requiredPath;
      } catch {
        return page.url === requiredPath;
      }
    };

    setCanView(pages.some((p) => matchPage(p) && p.can_view === true));
  }, []);

  const fetchClientSummary = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/client-summary/");

      setSummary({
        total_clients: res.data?.total_clients ?? 0,
        high_potential_clients: res.data?.high_potential_clients ?? 0,
        with_risk_clients: res.data?.with_risk_clients ?? "-",
        to_be_reactivated_clients: res.data?.to_be_reactivated_clients ?? "-",
      });
    } catch (error) {
      console.error(
        "Eroare la obținerea sumarului de clienți:",
        error.response?.data || error.message
      );
    }
  }, []);

  const fetchClientPortfolio = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/client-portfolio/");

      const mappedData = (res.data || [])
        .map((item) => ({
          id: item.id,
          client_code: item.client_code || "-",
          cui: item.cui || "-",
          name: item.name || "-",
          total_contracts_value: item.total_contracts_value ?? 0,
          total_contracts_system: item.total_contracts_system ?? 0,
          percentage: item.percentage ?? 0,
          status: item.status || "-",
          latest_event_date: item.latest_event_date || null,
        }))
        .sort((a, b) => {
          const statusPriority = {
            Active: 1,
            Prospect: 1,
            Blocked: 2,
            Inactive: 3,
          };

          const statusA = statusPriority[a.status] || 99;
          const statusB = statusPriority[b.status] || 99;

          return statusA - statusB;
        });

      setRows(mappedData);
    } catch (error) {
      console.error(
        "Eroare la obținerea portofoliului de clienți:",
        error.response?.data || error.message
      );
      setErrorMessage(error.response?.data?.message || error.message);
      setErrorDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    fetchClientSummary();
    fetchClientPortfolio();
  }, [fetchClientSummary, fetchClientPortfolio]);

  const handleViewClick = (row) => {
    navigate(`/client-portfolio-view/${row.id}`, {
      state: { client: row },
    });
  };

  const columns = [
    {
      field: "client_code",
      headerName: "Client Code",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Client Name",
      flex: 2,
      minWidth: 260,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "cui",
      headerName: "CUI",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },

    {
      field: "latest_event_date",
      headerName: "Last Interaction",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {formatDateRO(params.value)}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <StatusPill value={params.value} />
        </div>
      ),
    },
    {
      field: "percentage",
      headerName: "% of Total Value",
      flex: 1.1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            fontWeight: "600",
          }}
        >
          {formatPercentRO(params.value)}
        </div>
      ),
    },
  ];

  if (canView) {
    columns.push({
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleViewClick(params.row)}
          sx={{ color: "#1976d2" }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    });
  }

  return (
    <div className="client-portfolio-page">
      <div className="client-portfolio-container">
        <h2 className="title">CLIENT PORTFOLIO</h2>
      </div>

      <div className="client-summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">Clients</div>
          <div className="summary-card-value">{summary.total_clients}</div>
        </div>

        <div className="summary-card with-risk">
          <div className="summary-card-label">With Risk</div>
          <div className="summary-card-value">{summary.with_risk_clients}</div>
        </div>

        <div className="summary-card high-potential">
          <div className="summary-card-label">High Potential</div>
          <div className="summary-card-value">
            {summary.high_potential_clients}
          </div>
        </div>

        <div className="summary-card to-be-reactivated">
          <div className="summary-card-label">To be reactivated</div>
          <div className="summary-card-value">
            {summary.to_be_reactivated_clients}
          </div>
        </div>
      </div>

      <div className="table-container">
        <DataGrid
          id="client-portfolio-table"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 12, 20, 25]}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "1.4",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              whiteSpace: "normal",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important",
            },
          }}
        />
      </div>

      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" style={{ color: "red" }}>
          Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            sx={{
              backgroundColor: "#e0e0e0",
              color: "#424242",
              "&:hover": {
                backgroundColor: "#bdbdbd",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ClientPortfolio;